import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import PublicHeader from '../components/layout/PublicHeader';
import {
  ArrowLeft,
  CreditCard,
  Layout,
  Users,
  Mail,
  BarChart3,
  Sparkles,
  CheckCircle,
} from 'lucide-react';

/* ══════════════════════════════════════════════
   LANDING VARIANT B — "Clean Light"
   רקע בהיר עם mesh עדין, מוקאפ דפדפן בהטיה תלת־ממדית
   שמתיישרת בהובר, marquee יכולות ו-scroll reveals.
   ══════════════════════════════════════════════ */

function useReveal(threshold = 0.15) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          setVisible(true);
          obs.unobserve(el);
        }
      },
      { threshold, rootMargin: '9999px 0px 0px 0px' }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return { ref, visible };
}

const MARQUEE_ITEMS = [
  '💳 סליקת אשראי',
  '📄 קבלות אוטומטיות',
  '❤️ קמפיינים',
  '🤝 מתנדבים',
  '📧 דיוור מעוצב',
  '💬 WhatsApp',
  '📊 דוחות חיים',
  '🤖 AI מובנה',
  '🗓️ יומן משותף',
  '📈 תקציב ופיננסים',
  '👥 CRM לעמותות',
  '📱 SMS',
];

const FEATURES = [
  { icon: CreditCard, title: 'תרומות וסליקה', desc: 'סליקה מאובטחת, הוראות קבע וקבלות אוטומטיות — בזמן אמת.', grad: 'from-blue-500 to-blue-600' },
  { icon: Layout, title: 'דפי גיוס וקמפיינים', desc: 'בונים דף תרומה מרהיב בגרירה ושחרור, עם מעקב המרות.', grad: 'from-violet-500 to-purple-600' },
  { icon: Users, title: 'CRM ומתנדבים', desc: 'כל אנשי הקשר, ההיסטוריה והסגמנטציה — במקום אחד.', grad: 'from-emerald-500 to-teal-600' },
  { icon: Mail, title: 'דיוור, SMS ו-WhatsApp', desc: 'הודעות מעוצבות לתורמים עם תזמון אוטומטי ומעקב.', grad: 'from-rose-500 to-pink-600' },
  { icon: BarChart3, title: 'דוחות ופיננסים', desc: 'דשבורד אנליטי, תחזיות וייצוא לאקסל בלחיצה.', grad: 'from-amber-400 to-orange-500' },
  { icon: Sparkles, title: 'AI חכם', desc: 'כותב תוכן, מנתח נתונים ומציע אסטרטגיות גיוס.', grad: 'from-indigo-500 to-violet-600' },
];

const LandingVariantB: React.FC = () => {
  const features = useReveal();
  const product = useReveal();
  const cta = useReveal();

  return (
    <div dir="rtl" className="min-h-screen bg-[#F7F9FE] text-gray-900 font-heebo overflow-x-hidden">
      <PublicHeader />

      {/* ── Local keyframes ── */}
      <style>{`
        @keyframes bFadeUp { from { opacity: 0; transform: translateY(24px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes bFloat { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-12px); } }
        @keyframes bMarquee { from { transform: translateX(0); } to { transform: translateX(50%); } }
        @keyframes bBlob { 0%,100% { transform: translate(0,0) scale(1); } 33% { transform: translate(30px,-20px) scale(1.08); } 66% { transform: translate(-20px,15px) scale(0.95); } }
        .b-fade-up { animation: bFadeUp 0.8s ease-out both; }
        .b-delay-1 { animation-delay: 120ms; }
        .b-delay-2 { animation-delay: 240ms; }
        .b-delay-3 { animation-delay: 360ms; }
        .b-tilt { transform: perspective(1400px) rotateX(10deg) rotateY(-7deg) scale(0.98); transition: transform 0.7s cubic-bezier(0.22,1,0.36,1); }
        .b-tilt:hover { transform: perspective(1400px) rotateX(0deg) rotateY(0deg) scale(1); }
      `}</style>

      {/* ━━━━ HERO ━━━━ */}
      <section className="relative pt-20 pb-10 px-6 overflow-hidden">
        {/* Soft mesh blobs */}
        <div className="absolute -top-32 right-[-10%] w-[600px] h-[600px] bg-blue-200/50 rounded-full blur-[120px] pointer-events-none" style={{ animation: 'bBlob 14s ease-in-out infinite' }} />
        <div className="absolute top-40 left-[-8%] w-[500px] h-[500px] bg-violet-200/40 rounded-full blur-[110px] pointer-events-none" style={{ animation: 'bBlob 18s ease-in-out infinite reverse' }} />
        <div className="absolute bottom-0 right-1/3 w-[400px] h-[400px] bg-amber-100/50 rounded-full blur-[100px] pointer-events-none" />

        <div className="relative max-w-4xl mx-auto text-center">
          <div className="b-fade-up inline-flex items-center gap-2 px-4 py-1.5 mb-6 bg-white border border-blue-100 rounded-full shadow-sm">
            <Sparkles size={14} className="text-blue-600" />
            <span className="text-gray-600 text-sm font-semibold">הפלטפורמה החכמה לעמותות בישראל</span>
          </div>

          <h1 className="b-fade-up b-delay-1 text-4xl sm:text-5xl md:text-[64px] font-black leading-[1.12] tracking-tight mb-6">
            ניהול עמותות חכם
            <br />
            <span className="bg-gradient-to-l from-blue-600 via-indigo-600 to-violet-600 bg-clip-text text-transparent">
              הכל במקום אחד
            </span>
          </h1>

          <p className="b-fade-up b-delay-2 text-lg text-gray-600 leading-relaxed mb-8 max-w-xl mx-auto">
            תרומות, קמפיינים, CRM, דיוור, דוחות ובינה מלאכותית — מערכת אחת שנבנתה במיוחד לעמותות ישראליות.
          </p>

          <div className="b-fade-up b-delay-2 flex flex-col sm:flex-row gap-3 justify-center mb-6">
            <Link
              to="/register"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-gradient-to-l from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white text-base font-bold rounded-2xl shadow-[0_10px_35px_rgba(37,99,235,0.35)] hover:shadow-[0_14px_45px_rgba(37,99,235,0.45)] transition-all duration-300 hover:scale-[1.03]"
            >
              פתח חשבון בחינם
              <ArrowLeft size={18} />
            </Link>
            <a
              href="#features-b"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white border border-gray-200 text-gray-800 text-base font-semibold rounded-2xl hover:border-blue-300 hover:shadow-md transition-all duration-300"
            >
              למד עוד
            </a>
          </div>

          <div className="b-fade-up b-delay-3 flex flex-wrap justify-center gap-3 mb-4">
            {['🇮🇱 מיוצר בישראל', '✅ ביטול בכל עת', '🔒 סליקה מאובטחת PCI', '⚡ 7 ימי ניסיון חינם'].map(t => (
              <span key={t} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-gray-200 rounded-full text-gray-500 text-xs font-medium shadow-sm">
                {t}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* ━━━━ 3D TILT PRODUCT SHOT ━━━━ */}
      <section ref={product.ref} className="relative px-6 pb-10">
        <div className={`relative max-w-5xl mx-auto transition-all duration-1000 ${product.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-16'}`}>
          {/* Floating KPI chips */}
          <div className="hidden md:flex absolute -right-6 top-16 z-20 items-center gap-2.5 px-4 py-3 rounded-2xl bg-white shadow-xl border border-gray-100" style={{ animation: 'bFloat 5s ease-in-out infinite' }}>
            <div className="w-9 h-9 rounded-xl bg-emerald-100 flex items-center justify-center text-lg">💚</div>
            <div>
              <p className="text-[11px] text-gray-500 leading-tight">תרומה חדשה</p>
              <p className="text-sm font-black text-emerald-600 leading-tight">₪850</p>
            </div>
          </div>
          <div className="hidden md:flex absolute -left-6 bottom-24 z-20 items-center gap-2.5 px-4 py-3 rounded-2xl bg-white shadow-xl border border-gray-100" style={{ animation: 'bFloat 6s ease-in-out infinite', animationDelay: '1.2s' }}>
            <div className="w-9 h-9 rounded-xl bg-blue-100 flex items-center justify-center text-lg">📈</div>
            <div>
              <p className="text-[11px] text-gray-500 leading-tight">הכנסות החודש</p>
              <p className="text-sm font-black text-blue-600 leading-tight">₪8,250</p>
            </div>
          </div>

          {/* Browser frame with 3D tilt */}
          <div className="b-tilt rounded-3xl overflow-hidden border border-gray-200 bg-white shadow-[0_50px_100px_-30px_rgba(30,58,138,0.35)]">
            <div dir="ltr" className="flex items-center gap-2 px-4 py-2.5 bg-gray-50 border-b border-gray-200">
              <div className="flex gap-1.5">
                <span className="w-3 h-3 rounded-full bg-[#ff5f57]" />
                <span className="w-3 h-3 rounded-full bg-[#febc2e]" />
                <span className="w-3 h-3 rounded-full bg-[#28c840]" />
              </div>
              <div className="flex-1 flex justify-center">
                <div className="w-full max-w-[220px] rounded-md bg-white border border-gray-200 text-gray-400 text-[11px] text-center py-1 font-medium">app.amutech.co.il</div>
              </div>
              <div className="w-10" />
            </div>
            <img src="/screenshots/income-hero.png" alt="מסך הכנסות ותרומות — עמוטק" className="w-full h-auto block bg-[#f1f5f9]" />
          </div>
        </div>
      </section>

      {/* ━━━━ MARQUEE ━━━━ */}
      <section className="py-8 border-y border-gray-100 bg-white overflow-hidden" dir="ltr">
        <div className="flex w-max" style={{ animation: 'bMarquee 30s linear infinite' }}>
          {[...MARQUEE_ITEMS, ...MARQUEE_ITEMS].map((item, i) => (
            <span key={i} dir="rtl" className="mx-3 inline-flex items-center px-5 py-2.5 bg-gray-50 border border-gray-100 rounded-full text-gray-600 text-sm font-semibold whitespace-nowrap">
              {item}
            </span>
          ))}
        </div>
      </section>

      {/* ━━━━ FEATURES ━━━━ */}
      <section id="features-b" ref={features.ref} className="py-24 px-6">
        <div className="max-w-6xl mx-auto">
          <div className={`text-center mb-14 transition-all duration-700 ${features.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-black tracking-tight mb-4">כל מה שעמותה צריכה</h2>
            <p className="text-lg text-gray-500 max-w-xl mx-auto">כלים מקצועיים בממשק אחד פשוט ונוח.</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {FEATURES.map((f, i) => {
              const Icon = f.icon;
              return (
                <div
                  key={f.title}
                  className={`group p-7 rounded-3xl bg-white border border-gray-100 shadow-sm hover:shadow-xl hover:-translate-y-1.5 transition-all duration-500 ${features.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                  style={{ transitionDelay: `${i * 90}ms` }}
                >
                  <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${f.grad} flex items-center justify-center mb-5 shadow-lg group-hover:scale-110 group-hover:rotate-3 transition-transform duration-300`}>
                    <Icon size={26} className="text-white" />
                  </div>
                  <h3 className="text-xl font-bold mb-2">{f.title}</h3>
                  <p className="text-gray-500 leading-relaxed text-[15px]">{f.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ━━━━ CTA ━━━━ */}
      <section ref={cta.ref} className="px-6 pb-24">
        <div className={`relative max-w-5xl mx-auto rounded-[32px] overflow-hidden bg-gradient-to-l from-blue-600 via-indigo-600 to-violet-600 px-8 py-16 text-center transition-all duration-700 ${cta.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="absolute -top-20 right-10 w-[300px] h-[300px] bg-white/10 rounded-full blur-[80px]" />
          <div className="absolute -bottom-24 left-10 w-[300px] h-[300px] bg-white/10 rounded-full blur-[80px]" />
          <h2 className="relative text-3xl sm:text-4xl font-black text-white tracking-tight mb-4">
            מוכנים להתחיל לגייס יותר?
          </h2>
          <p className="relative text-blue-100 text-lg mb-8">7 ימי ניסיון חינם · ללא כרטיס אשראי · ביטול בכל עת</p>
          <Link
            to="/register"
            className="relative inline-flex items-center gap-2 px-10 py-4 bg-white text-blue-700 text-lg font-bold rounded-2xl shadow-xl hover:shadow-2xl hover:scale-[1.04] transition-all duration-300"
          >
            פתח חשבון בחינם
            <ArrowLeft size={20} />
          </Link>
          <div className="relative flex flex-wrap justify-center gap-5 mt-8 text-blue-100 text-sm">
            {['ביטול בכל עת', 'שרתים בישראל', 'תמיכה בעברית'].map(t => (
              <span key={t} className="inline-flex items-center gap-1.5"><CheckCircle size={15} /> {t}</span>
            ))}
          </div>
        </div>
      </section>

      {/* ── Mini footer ── */}
      <footer className="py-8 px-6 border-t border-gray-100 bg-white">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-3 text-sm text-gray-500">
          <p>&copy; 2026 עמוטק – פתרונות דיגיטליים לעמותות בע"מ</p>
          <div className="flex gap-4">
            <Link to="/terms" className="hover:text-gray-900 transition-colors">תנאי שימוש</Link>
            <Link to="/privacy" className="hover:text-gray-900 transition-colors">מדיניות פרטיות</Link>
            <Link to="/accessibility" className="hover:text-gray-900 transition-colors">נגישות</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingVariantB;
